import { useTranslation } from 'next-i18next';

import { CurrencyCode } from '../../../types/Locations';
import { FramingSection } from '../Framing/FramingSection';
import { FramingProduct, FramingSelections } from '../Framing/types';
import {
  OrderReviewSummary,
  OrderReviewSummaryProps,
} from '../OrderReview/OrderReviewSummary';
import { MaxQuantitySelector } from './ui/MaxQuantitySelector';
import { PreOrderOpportunityBanner } from './ui/PreOrderOpportunityBanner';
import { WorkSelectionGrid } from './ui/WorkSelectionGrid';

export type OrderReviewSummaryViewProps = Omit<
  OrderReviewSummaryProps,
  'showPreview'
>;

export type WorksProps = {
  artistName: string;
  currencyCode: CurrencyCode;
  framingProducts: FramingProduct[];
  maxQuantity: number;
  itemInOrderIds: string[];
  enteredItemIds?: string[];
  isAuction?: boolean;
  isLinkedDraw?: boolean;
  isPreOrderFlow: boolean;
  preOrdersEnabled: boolean;
  allowMultipleSelections?: boolean;
  showFramingErrors?: boolean;
  summaryProps: OrderReviewSummaryViewProps;
  onFramingChange: (selections: FramingSelections) => void;
  onMaxQuantityChange: (maxQuantity: number) => void;
  onToggleItem: (itemId: string, quantity?: number) => void;
  items: {
    id: string;
    title: string;
    price: number;
    imageSrc: string;
    imageAlt: string;
    quantity?: number;
    quantityLimit?: number;
    allowMultiple?: boolean;
    soldOut?: boolean;
  }[];
};

export const Works = ({
  allowMultipleSelections,
  artistName,
  currencyCode,
  enteredItemIds,
  framingProducts,
  isAuction = false,
  isLinkedDraw = false,
  isPreOrderFlow,
  itemInOrderIds,
  items,
  maxQuantity,
  onFramingChange,
  onMaxQuantityChange,
  onToggleItem,
  preOrdersEnabled,
  showFramingErrors = false,
  summaryProps,
}: WorksProps) => {
  const { t } = useTranslation('checkout');

  return (
    <>
      <h2 className="text-center text-md font-bold text-neutral-6 lg:text-lg">
        {allowMultipleSelections
          ? t('orderReview.buy.title')
          : isAuction
            ? t('orderReview.auction.title', { artistName })
            : isPreOrderFlow
              ? t('orderReview.preOrder.title', { artistName })
              : t('orderReview.titleForArtist', { artistName })}
      </h2>

      {preOrdersEnabled && isLinkedDraw && (
        <PreOrderOpportunityBanner
          images={items.map((item) => ({
            src: item.imageSrc,
            alt: item.imageAlt,
          }))}
        />
      )}

      <WorkSelectionGrid
        allowMultipleSelections={allowMultipleSelections}
        currencyCode={currencyCode}
        enteredItemIds={enteredItemIds}
        isPreOrderFlow={isPreOrderFlow}
        onToggleItem={onToggleItem}
        itemInOrderIds={itemInOrderIds}
        items={items}
        showHeading={
          items.length > 1 && (allowMultipleSelections || isLinkedDraw)
        }
        showPreference={isLinkedDraw}
      />

      {isLinkedDraw && (
        <MaxQuantitySelector
          itemCount={items.length}
          maxQuantity={maxQuantity}
          onChange={onMaxQuantityChange}
          selectedCount={itemInOrderIds.length}
        />
      )}

      {framingProducts.length > 0 && (
        <FramingSection
          currencyCode={currencyCode}
          onSelectionChange={onFramingChange}
          products={framingProducts}
          showErrors={showFramingErrors}
        />
      )}

      <div className="lg:hidden">
        <OrderReviewSummary {...summaryProps} />
      </div>
    </>
  );
};
